<template>
    <DhfHeader
        :data="headerNavContents"
        :cart-contents="cartContents"
        :cart-count="cartCount"
        :cart-status="cartStatus"
        :account-authenticated="isAuthenticated"
        :customer-data="customerData"
        :has-logged-out="hasLoggedOut"
        :sign-out-action="handleSignOut"
        :persistent="persistent"
        :minimal="useMinimalHeader"
        :disable-nav-icons="useDisabledNavIcons"
        :language-object="props?.languageObject"
        @handle-mini-cart-click="handleMiniCartClick"
    />
    <UmfModalsController
        v-if="resolvedGlobals"
        :globals="resolvedGlobals"
        :feature-toggle-configs="featureToggleConfigs"
    />
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch, watchEffect, provide } from 'vue';
import { UmfModalsController } from '@miele/user-management-frontend';
import DhfHeader from '@/components/dhf-header/dhf-header.vue';
import useAccount from '@/composables/useAccount';
import useAuth from '@/composables/useAuth';
import useCart from '@/composables/useCart';
import useConfiguration from '@/composables/useConfiguration';
import { useNavigationQuery } from '@/composables/useNavigation';
import { useTracking } from '@/composables/useTracking';
import { CART_UPDATED_EVENT } from '@/constants/events.constants';
import { EventBus } from '@/lib/EventBus/event-bus';
import MieleApiService, { type Instance as MieleApiServiceInstance } from '@/services/miele-api-service';
import type { IHeaderAppProps, HeaderProps } from '@/types/shared-types';
import { type MieleStoredGlobals } from '@/types/shared-types';
import DomUtils from '@/utils/domUtils';
import { useFeatureToggleQuery } from '@/composables/useFeatureToggleConfiguration';

const props = withDefaults(defineProps<IHeaderAppProps>(), {
    globalsConfig: undefined,
    baseUrl: undefined,
    useDevNavigation: false,
    navigationData: undefined,
    persistent: true,
    minimal: false,
    logoOnly: false,
    isGtmEnabled: false,
    disableNavIcons: false,
    languageObject: undefined,
    featureToggleData: undefined,
});

const headerNavContents = ref<HeaderProps | null>(null);
const mieleService = ref<MieleApiServiceInstance>();
const resolvedGlobals = ref<MieleStoredGlobals>(props.globalsConfig);
const isSessionAuthenticated = ref<boolean>(false);
const featureToggleConfigs = ref();

// For context check ECOMNOW-1654
const { setEnabled } = useTracking();
watch(
    () => props.isGtmEnabled,
    () => {
        setEnabled(props.isGtmEnabled);
    }
);

const useMinimalHeader = computed(() => {
    if (props.logoOnly) return true;
    if (typeof props.minimal === 'boolean') return props.minimal;
    return props.minimal.toLowerCase() === 'true';
});

const useDisabledNavIcons = computed(() => {
    if (props.logoOnly) return true;
    if (typeof props.disableNavIcons === 'boolean') return props.disableNavIcons;
    return (props.disableNavIcons as string).toLowerCase() === 'true';
});

// Sets up a ref for the get cart handler (see onMounted),
// and an exposed wrapper for triggering refresh via component ref
const refreshCartHandler = ref<() => Promise<void>>();
const refreshCart = () => {
    if (props.logoOnly) return;
    if (refreshCartHandler.value) refreshCartHandler.value();
};

const {
    isAuthenticated,
    fetchCustomerData,
    hasLoggedOut,
    customerData,
    signOutAction,
    status: customerDataStatus,
} = useAccount();
const { checkAuthentication, sessionSignOut, setAuthConfig } = useAuth();
const { cartContents, cartCount, cartStatus, getCart, getCartCount } = useCart();

// Headless apps may pass in the navigation object, which
// should be preferred over component fetching for SSR
if (!props.navigationData && !props.logoOnly) {
    const { data } = useNavigationQuery({
        useDev: props.useDevNavigation || false,
        baseUrl: props.baseUrl,
    });

    if (data.value?.header) headerNavContents.value = data.value.header;

    watch(data, () => {
        if (data.value?.header) headerNavContents.value = data.value.header;
    });
} else {
    headerNavContents.value = props.navigationData ?? null;
}

// Skip fetching when headless apps has passed on the feature toggle configurations
if (!props.featureToggleData) {
    const { data: featuresData } = useFeatureToggleQuery();
    featureToggleConfigs.value = featuresData.value;

    watch(featuresData, () => {
        if (featuresData.value) featureToggleConfigs.value = featuresData.value;
    });
} else {
    featureToggleConfigs.value = props?.featureToggleData;
}

// make feature toggle configuration available throughout all child components
provide('featureToggleConfigs', featureToggleConfigs);

/**
 * Since this component is also consumed by Nuxt App with SSR. We need
 * to make sure window/document accessing code runs client side only.
 */
onMounted(() => {
    const { globalsConfig, loadConfig } = useConfiguration(props?.globalsConfig);
    loadConfig().then(() => {
        if (!globalsConfig.value) return;

        const cookieNoticeUrl = globalsConfig.value['module.cookieNotice.url'];
        if (cookieNoticeUrl) {
            import('@/utils/handleCookieNotice').then(({ handleCookieNotice }) => {
                handleCookieNotice(cookieNoticeUrl);
            });
        }

        if (props.logoOnly) return;
        resolvedGlobals.value = globalsConfig.value;
        mieleService.value = new MieleApiService(globalsConfig.value);
        setAuthConfig(globalsConfig.value);

        checkAuthentication().then((authenticated: boolean) => {
            if (!globalsConfig.value) return;
            if (authenticated) {
                isSessionAuthenticated.value = authenticated;
                handleAccount();
            }
            handleCartCount(authenticated, globalsConfig.value);
        });
    });

    // Set class on body to remove margin for non persistant headers
    watchEffect(() => {
        DomUtils.toggleMinimalBodyClass(useMinimalHeader.value || !props.persistent);
    });

    window.EventBus = window.EventBus || new EventBus();
    window.EventBus.register(CART_UPDATED_EVENT, refreshCart);
});

const handleCart = (authenticated: boolean, config: MieleStoredGlobals) => {
    if (!mieleService.value) return;
    getCart(mieleService.value, config['module.eComApi.SiteId'], authenticated);
};

const handleCartCount = (authenticated: boolean, config: MieleStoredGlobals) => {
    if (!mieleService.value) return;
    getCartCount(mieleService.value, config['module.eComApi.SiteId'], authenticated);

    // Setup the refesh cart handler
    refreshCartHandler.value = async () => {
        if (!mieleService.value) return;
        const stillAuthenticated = await checkAuthentication();
        handleAccount();
        await getCartCount(mieleService.value, config['module.eComApi.SiteId'], stillAuthenticated);
    };
};

const handleAccount = () => {
    if (!mieleService.value) return;
    fetchCustomerData(mieleService.value);
};

const handleSignOut = async () => {
    const signoutSuccess = await sessionSignOut();
    if (signoutSuccess) {
        signOutAction();
        refreshCart();
    }
};

const handleMiniCartClick = () => {
    handleCart(isSessionAuthenticated.value, resolvedGlobals.value);
};

defineExpose({
    refreshCart,
    customerData,
    customerDataStatus,
});
</script>

<style lang="scss">
@import './styles/app.scss';
</style>
